import React from 'react';
import { getCountryByCode, countries } from '@/data/countries';
import { resolveCountryName } from '@/utils/countryResolver';

interface CountryFlagProps {
  /** ISO2, ISO3, or full country name */
  code?: string | { code?: unknown; name?: unknown } | null;
  showName?: boolean;
  className?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const SIZE_MAP = {
  xs: { width: 32, imgClass: 'w-4 h-4', fallbackClass: 'w-4 h-4 text-[10px]' },
  sm: { width: 40, imgClass: 'w-5 h-5', fallbackClass: 'w-5 h-5 text-xs' },
  md: { width: 48, imgClass: 'w-6 h-6', fallbackClass: 'w-6 h-6 text-sm' },
  lg: { width: 64, imgClass: 'w-8 h-8', fallbackClass: 'w-8 h-8 text-base' },
  xl: { width: 80, imgClass: 'w-10 h-10', fallbackClass: 'w-10 h-10 text-lg' },
};

/** Returns a flagcdn.com URL for a given ISO2 country code. Works on ALL platforms. */
export function getFlagUrl(iso2: string, width = 40): string {
  return `https://flagcdn.com/w${width}/${iso2.toLowerCase()}.png`;
}

/** Returns a flagcdn.com srcSet for retina displays. */
export function getFlagSrcSet(iso2: string, width = 40): string {
  const code = iso2.toLowerCase();
  return `https://flagcdn.com/w${width}/${code}.png 1x, https://flagcdn.com/w${width * 2}/${code}.png 2x`;
}

function getFlagEmoji(iso2: string): string {
  const normalized = iso2.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) return normalized;
  return String.fromCodePoint(...normalized.split('').map(char => 127397 + char.charCodeAt(0)));
}

function UaeRoundFlag({ className }: { className: string }) {
  return (
    <span
      aria-hidden="true"
      className={`${className} inline-flex shrink-0 overflow-hidden rounded-full ring-1 ring-border/40 shadow-sm`}
    >
      <svg viewBox="0 0 100 100" className="block h-full w-full" role="img" aria-label="AE">
        <defs>
          <clipPath id="uae-flag-circle">
            <circle cx="50" cy="50" r="50" />
          </clipPath>
          <linearGradient id="uae-flag-shade" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0" stopColor="white" stopOpacity="0.16" />
            <stop offset="0.54" stopColor="white" stopOpacity="0" />
            <stop offset="1" stopColor="black" stopOpacity="0.16" />
          </linearGradient>
        </defs>
        <g clipPath="url(#uae-flag-circle)">
          <rect width="100" height="100" fill="#ef3340" />
          <rect x="31" width="69" height="33.34" fill="#009739" />
          <rect x="31" y="33.33" width="69" height="33.34" fill="#ffffff" />
          <rect x="31" y="66.66" width="69" height="33.34" fill="#000000" />
          <rect width="100" height="100" fill="url(#uae-flag-shade)" />
        </g>
      </svg>
    </span>
  );
}

/**
 * Inline flag image helper — replaces all emoji flag rendering.
 * Works on Windows, Linux, macOS — all browsers.
 */
export const FlagImg = React.forwardRef<HTMLElement, {
  code: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}>(function FlagImg({
  code,
  size = 'sm',
  className,
}, ref) {
  const [hasError, setHasError] = React.useState(false);
  if (!code) return null;

  const { imgClass, width, fallbackClass } = SIZE_MAP[size];
  const normalizedCode = code.toLowerCase();

  if (normalizedCode === 'ae') {
    return <UaeRoundFlag className={`${imgClass} ${className ?? ''}`} />;
  }

  if (hasError) {
    return (
      <span
        ref={ref as React.Ref<HTMLSpanElement>}
        aria-hidden="true"
        className={`${fallbackClass} inline-flex items-center justify-center bg-secondary/60 text-foreground shrink-0 overflow-hidden ring-1 ring-border/40 shadow-sm ${className ?? ''}`}
        style={{ borderRadius: '9999px' }}
      >
        {getFlagEmoji(code)}
      </span>
    );
  }

  // Some flags (notably UAE 🇦🇪) have an off-centre design — the red vertical
  // bar sits hard on the left edge — so `object-cover` inside a circular
  // container crops it away. For these flags we use `object-contain` with
  // a neutral background so the whole flag is visible inside the circle.
  const offCentre = ['kw', 'jo', 'ps', 'sd', 'ye', 'om'].includes(normalizedCode);
  const fitClass = offCentre ? 'object-contain bg-white' : 'object-cover';

  return (
    <img
      ref={ref as React.Ref<HTMLImageElement>}
      src={getFlagUrl(code, width)}
      srcSet={getFlagSrcSet(code, width)}
      alt={code.toUpperCase()}
      aria-hidden="true"
      draggable={false}
      className={`${imgClass} inline-block ${fitClass} shrink-0 ring-1 ring-border/40 shadow-sm ${className ?? ''}`}
      style={{ borderRadius: '9999px' }}
      loading="lazy"
      onError={() => setHasError(true)}
    />
  );
});

FlagImg.displayName = 'FlagImg';

/**
 * Renders a country flag as an IMAGE (not emoji). Works on Windows, Linux, macOS.
 * Accepts ISO2, ISO3, or full country name strings.
 */
export function CountryFlag({ code, showName = true, className, size = 'sm' }: CountryFlagProps) {
  const countryValue = typeof code === 'string'
    ? code.trim()
    : code && typeof code === 'object'
      ? (typeof code.code === 'string' && code.code.trim()) ||
        (typeof code.name === 'string' && code.name.trim()) || ''
      : '';

  if (!countryValue) return <span className="text-muted-foreground">—</span>;

  // Try direct ISO2 lookup first
  const direct = getCountryByCode(countryValue.toUpperCase());
  if (direct) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className ?? ''}`}>
        <FlagImg code={direct.code} size={size} />
        {showName && <span>{direct.name}</span>}
      </span>
    );
  }

  // Resolve ISO3 or full name → canonical name, then look up
  const resolvedName = resolveCountryName(countryValue);
  const byName = countries.find(c => c.name === resolvedName);
  if (byName) {
    return (
      <span className={`inline-flex items-center gap-1.5 ${className ?? ''}`}>
        <FlagImg code={byName.code} size={size} />
        {showName && <span>{byName.name}</span>}
      </span>
    );
  }

  // Last resort: plain text
  return <span className={className}>{resolvedName || countryValue}</span>;
}
